import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import { createStream } from './stream.js';
import { useAuth } from './AuthContext.jsx';

const RealtimeContext = createContext(null);

/**
 * Holds the single live connection for the session and hands events to whoever
 * is interested. One stream serves the whole app — the chat panel, the unread
 * badges and the toast layer all read from it.
 */
export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('idle');
  const [unread, setUnread] = useState({ total: 0, byBooking: {} });
  const handlers = useRef(new Map());

  const emit = useCallback((event, data) => {
    for (const fn of handlers.current.get(event) || []) {
      try {
        fn(data);
      } catch (err) {
        console.error('realtime handler failed', err);
      }
    }
  }, []);

  /** Register a listener; returns an unsubscribe function. */
  const on = useCallback((event, fn) => {
    if (!handlers.current.has(event)) handlers.current.set(event, new Set());
    handlers.current.get(event).add(fn);
    return () => handlers.current.get(event)?.delete(fn);
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      setUnread(await api.get('/api/messages/unread'));
    } catch {
      /* keep the last known counts */
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread({ total: 0, byBooking: {} });
      setStatus('idle');
      return undefined;
    }

    refreshUnread();

    const close = createStream({
      onStatus: setStatus,
      onEvent: (event, data) => {
        if (event === 'message' && !data.self) {
          // Optimistically bump the badge; the chat panel clears it on open.
          setUnread((u) => ({
            total: u.total + 1,
            byBooking: { ...u.byBooking, [data.booking_id]: (u.byBooking[data.booking_id] || 0) + 1 },
          }));
        }
        emit(event, data);
      },
    });

    return close;
  }, [user, emit, refreshUnread]);

  /** Called by the chat panel once it has displayed a conversation. */
  const clearUnread = useCallback((bookingId) => {
    setUnread((u) => {
      const n = u.byBooking[bookingId] || 0;
      if (!n) return u;
      const byBooking = { ...u.byBooking };
      delete byBooking[bookingId];
      return { total: Math.max(u.total - n, 0), byBooking };
    });
  }, []);

  const value = useMemo(
    () => ({ status, unread, on, refreshUnread, clearUnread }),
    [status, unread, on, refreshUnread, clearUnread]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export const useRealtime = () => {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used inside <RealtimeProvider>');
  return ctx;
};

/** Convenience hook: subscribe to one realtime event for the life of a component. */
export function useRealtimeEvent(event, handler) {
  const { on } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => on(event, (data) => ref.current?.(data)), [on, event]);
}
